import { Injectable, TemplateRef } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, publishReplay, refCount } from 'rxjs/operators';
import {
  checkZendeskIsOpen,
  closeZendeskLauncher,
  getZendeskLauncher,
  openZendeskLauncher,
  probeForZendeskLauncherForSideDrawer,
} from '../../lib/zendesk';
import { displayPendingChangesDialog } from '../core/guards/pending-changes-guard.service';

export interface DynamicOpenCloseTemplateRef {
  id: string;
  templateRef: TemplateRef<any>;
}

@Injectable({ providedIn: 'root' })
export class DynamicOpenCloseTemplateRefService {
  private templateMap = new Map<string, TemplateRef<any>>();
  private openTemplate$$ = new BehaviorSubject<string>('');
  private hasPendingChanges$$ = new BehaviorSubject<boolean>(false);
  openTemplate$: Observable<DynamicOpenCloseTemplateRef>;
  open$: Observable<boolean>;
  isZendeskOpen = false;

  constructor() {
    this.openTemplate$ = this.openTemplate$$.pipe(
      map((t) => {
        const templateRef = this.templateMap.get(t);
        const id = templateRef ? t : '';
        return {
          id: id,
          templateRef: templateRef,
        };
      }),
      publishReplay(1),
      refCount(),
    );

    this.open$ = this.openTemplate$$.pipe(map((a) => !!a));
  }

  updatePendingChanges(hasChanges: boolean): void {
    this.hasPendingChanges$$.next(hasChanges);
  }

  close(id?: string): void {
    let discardChanges = true;
    if (this.hasPendingChanges$$.getValue()) {
      discardChanges = displayPendingChangesDialog();
    }
    if (!discardChanges) {
      return;
    }

    if (id) {
      if (this.openTemplate$$.getValue() === id) {
        this.openTemplate$$.next('');
      }
    } else {
      this.openTemplate$$.next('');
    }

    const launcher = getZendeskLauncher();
    if (!!launcher && this.isZendeskOpen) {
      openZendeskLauncher();
    }
  }

  registerTemplate(id: string, template: TemplateRef<any>): void {
    this.templateMap.set(id, template);
    if (this.openTemplate$$.getValue() === id) {
      this.open(id);
    }
  }

  open(id: string): void {
    window.setTimeout(() => {
      this.openTemplate$$.next(id);
      this.isZendeskOpen = checkZendeskIsOpen();

      const launcher = getZendeskLauncher();
      if (launcher) {
        closeZendeskLauncher();
      } else {
        probeForZendeskLauncherForSideDrawer();
      }
    });
  }

  toggle(id: string): void {
    const launcher = getZendeskLauncher();
    if (this.openTemplate$$.getValue() === id) {
      this.openTemplate$$.next('');
      if (!!launcher && this.isZendeskOpen) {
        openZendeskLauncher();
      }
    } else {
      this.openTemplate$$.next(id);
      this.isZendeskOpen = checkZendeskIsOpen();
      if (launcher) {
        closeZendeskLauncher();
      } else {
        probeForZendeskLauncherForSideDrawer();
      }
    }
  }
}
